/**
 * The SYQEL® mark — ONE rule for every web-rendered surface (syqel.art, the desktop shell, the phone
 * webviews via Visualizer, and the Android TV / Fire TV / webOS receiver via TvReceiver): while the
 * viewer is not entitled the mark is ALWAYS on, large and centred, printed INTO the scene pixels by the
 * engine's VisualHost (pre-PostFX — dev-tools-proof). Entitlement removes it.
 *
 * `isSubscribed()` is the single source of truth: the cookie/StoreKit cache, the native shells'
 * `window.__syqelEntitled`, and a casting sender's runtime entitlement (which reverts when the sender
 * leaves) all land there — so the mark comes back by itself when a subscribed sender disconnects.
 * The native tvOS receiver mirrors this in WatermarkView (SyqelArtTVApp.swift): same word, same 0.26.
 */
import { isSubscribed } from './catalogue'

export const WATERMARK_TEXT = 'SYQEL®'
export const WATERMARK_OPACITY = 0.45 // owner, on a real phone: 0.26 read too faint once the mark was sized down (engine 0.15.2)
const FADE_SEC = 0.6 // a soft ease whenever the mark appears or leaves — never a pop

/** The two VisualHost fields the mark drives. */
interface WatermarkTarget {
  watermark: string | null
  watermarkOpacity: number
}

/**
 * Per-render-loop driver: call once per frame with the frame's dt (seconds). `gated` = someone the mark
 * applies to is watching — always true on a player (and the phone sender); on a TV receiver only while
 * a sender's audio is actually FLOWING (receiver/session.ts audioLive). With nothing casting — no sender, or a
 * connected one sending nothing — the TV shows its "Cast to TV" notice instead.
 */
export function createWatermark(): (host: WatermarkTarget, dt: number, gated?: boolean) => void {
  let alpha = 0
  return (host, dt, gated = true) => {
    const target = gated && !isSubscribed() ? 1 : 0
    const step = dt / FADE_SEC
    alpha = target > alpha ? Math.min(target, alpha + step) : Math.max(target, alpha - step)
    host.watermark = alpha > 0 ? WATERMARK_TEXT : null
    host.watermarkOpacity = WATERMARK_OPACITY * alpha
  }
}
